import React, { useCallback, useEffect, useState } from 'react';
import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import RefreshIcon from '@mui/icons-material/Refresh';
import AndroidIcon from '@mui/icons-material/Android';
import AppleIcon from '@mui/icons-material/Apple';
import HistoryIcon from '@mui/icons-material/History';
import { theme } from '../../styles/theme';
import { countOpened, fetchCampaignsPage } from '../../services/pushCampaigns';
import { TONE_COLORS, campaignStatusLabel, formatDateTime, percent } from '../../services/pushMetrics';
import type { CampaignStatus, PushCampaign } from '../../types/push';

interface Props {
  onOpen: (campaign: PushCampaign) => void;
}

const STATUS_FILTERS: { value: 'all' | CampaignStatus; label: string }[] = [
  { value: 'all', label: 'Todos os status' },
  { value: 'sent', label: 'Concluídas' },
  { value: 'sending', label: 'Enviando / interrompidas' },
  { value: 'scheduled', label: 'Agendadas' },
  { value: 'failed', label: 'Falharam' },
  { value: 'canceled', label: 'Canceladas' },
];

/** Abertas de uma campanha: congeladas no documento, contadas no servidor, ou "—" sem app que reporte. */
const OpenedCell: React.FC<{ campaign: PushCampaign }> = ({ campaign }) => {
  const trackable = campaign.stats?.trackable ?? 0;
  const frozen = campaign.metricsFrozenAt ? campaign.stats?.opened ?? 0 : null;
  const [opened, setOpened] = useState<number | null>(frozen);

  useEffect(() => {
    if (trackable === 0 || frozen !== null) return;
    countOpened(campaign.id).then(setOpened).catch(() => setOpened(null));
  }, [campaign.id, trackable, frozen]);

  if (trackable === 0) {
    return <span style={styles.muted} title="A abertura passa a ser registrada a partir do app 1.12">—</span>;
  }
  if (opened === null) return <span style={styles.muted}>…</span>;
  return (
    <span>
      {opened} <span style={styles.muted}>({percent(opened, trackable)})</span>
    </span>
  );
};

/** Lista de campanhas enviadas pelo painel, no estilo da aba Campanhas do console do Firebase. */
const PushHistory: React.FC<Props> = ({ onOpen }) => {
  const [campaigns, setCampaigns] = useState<PushCampaign[]>([]);
  const [cursor, setCursor] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showTests, setShowTests] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | CampaignStatus>('all');

  const load = useCallback(async (after: QueryDocumentSnapshot<DocumentData> | null) => {
    try {
      setLoading(true);
      const page = await fetchCampaignsPage(after);
      setCampaigns(prev => (after ? [...prev, ...page.campaigns] : page.campaigns));
      setCursor(page.cursor);
      setHasMore(page.hasMore);
    } catch (error) {
      console.error('❌ Erro ao carregar histórico:', error);
      alert('Erro ao carregar o histórico: ' + (error instanceof Error ? error.message : 'Erro desconhecido'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(null);
  }, [load]);

  const visible = campaigns.filter(campaign => {
    if (!showTests && campaign.isTest) return false;
    if (statusFilter === 'all') return true;
    return campaign.status === statusFilter;
  });

  const platformIcon = (campaign: PushCampaign) => {
    if (campaign.audience?.type !== 'segment') return null;
    if (campaign.audience.platform === 'android') return <AndroidIcon style={{ fontSize: 16, color: '#3DDC84' }} />;
    if (campaign.audience.platform === 'ios') return <AppleIcon style={{ fontSize: 16 }} />;
    return null;
  };

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Histórico de campanhas</h2>
          <div style={styles.subtitle}>
            Só aparecem os envios feitos por este painel. Campanhas criadas no console do Firebase não são listadas.
          </div>
        </div>
        <button style={styles.iconButton} onClick={() => load(null)} title="Atualizar" disabled={loading}>
          <RefreshIcon />
        </button>
      </div>

      <div style={styles.filters}>
        <select
          style={styles.select}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as 'all' | CampaignStatus)}
        >
          {STATUS_FILTERS.map(filter => (
            <option key={filter.value} value={filter.value}>{filter.label}</option>
          ))}
        </select>
        <label style={styles.toggle}>
          <input
            type="checkbox"
            checked={showTests}
            onChange={(e) => setShowTests(e.target.checked)}
            style={styles.checkbox}
          />
          Mostrar testes
        </label>
      </div>

      {!loading && visible.length === 0 ? (
        <div style={styles.empty}>
          <HistoryIcon style={{ fontSize: 64, opacity: 0.4, color: theme.colors.primary }} />
          <p>Nenhuma campanha {campaigns.length ? 'com esses filtros' : 'enviada pelo painel ainda'}</p>
        </div>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Campanha</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Público</th>
                <th style={styles.th}>Data</th>
                <th style={styles.thNumber}>Enviadas</th>
                <th style={styles.thNumber}>Abertas</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(campaign => {
                const status = campaignStatusLabel(campaign);
                return (
                  <tr key={campaign.id} style={styles.row} onClick={() => onOpen(campaign)}>
                    <td style={styles.td}>
                      <div style={styles.name}>
                        {campaign.name}
                        {campaign.isTest && <span style={styles.testChip}>Teste</span>}
                      </div>
                      <div style={styles.preview}>
                        {campaign.name !== campaign.title && <strong>{campaign.title} · </strong>}
                        {campaign.body}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.chip, ...TONE_COLORS[status.tone] }}>{status.label}</span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.audience}>{platformIcon(campaign)}{campaign.audienceLabel}</span>
                    </td>
                    <td style={styles.td}>
                      {campaign.status === 'scheduled'
                        ? `Para ${formatDateTime(campaign.scheduledAt)}`
                        : formatDateTime(campaign.sentAt || campaign.createdAt)}
                    </td>
                    <td style={styles.tdNumber}>
                      {campaign.stats ? `${campaign.stats.sent} de ${campaign.stats.targeted}` : '—'}
                    </td>
                    <td style={styles.tdNumber}>
                      <OpenedCell campaign={campaign} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {loading && <div style={styles.loading}>Carregando...</div>}
      {!loading && hasMore && (
        <button style={styles.moreButton} onClick={() => load(cursor)}>Carregar mais</button>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  card: {
    background: 'white',
    padding: '30px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '15px',
    marginBottom: '20px',
  },
  title: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: theme.colors.primary,
    margin: 0,
  },
  subtitle: {
    fontSize: '13px',
    color: '#999',
    marginTop: '6px',
  },
  iconButton: {
    padding: '8px',
    borderRadius: '8px',
    border: `2px solid ${theme.colors.primary}`,
    background: 'transparent',
    color: theme.colors.primary,
    cursor: 'pointer',
    display: 'flex',
  },
  filters: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    flexWrap: 'wrap',
    marginBottom: '20px',
  },
  select: {
    padding: '10px 15px',
    fontSize: '14px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    outline: 'none',
    cursor: 'pointer',
  },
  toggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: theme.colors.text,
    cursor: 'pointer',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  th: {
    textAlign: 'left',
    padding: '10px 12px',
    color: theme.colors.secondary,
    fontWeight: 600,
    borderBottom: '2px solid #eee',
    whiteSpace: 'nowrap',
  },
  thNumber: {
    textAlign: 'right',
    padding: '10px 12px',
    color: theme.colors.secondary,
    fontWeight: 600,
    borderBottom: '2px solid #eee',
    whiteSpace: 'nowrap',
  },
  row: {
    cursor: 'pointer',
  },
  td: {
    padding: '14px 12px',
    borderBottom: '1px solid #f0f0f0',
    verticalAlign: 'top',
  },
  tdNumber: {
    padding: '14px 12px',
    borderBottom: '1px solid #f0f0f0',
    verticalAlign: 'top',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  name: {
    fontWeight: 'bold',
    color: theme.colors.primary,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  preview: {
    color: '#666',
    fontSize: '13px',
    marginTop: '4px',
    maxWidth: '420px',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  testChip: {
    padding: '2px 8px',
    borderRadius: '10px',
    background: '#f0f0f0',
    color: '#757575',
    fontSize: '11px',
    fontWeight: 'bold',
  },
  chip: {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
  },
  audience: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    whiteSpace: 'nowrap',
  },
  muted: {
    color: '#999',
  },
  empty: {
    textAlign: 'center',
    padding: '40px',
    color: theme.colors.text,
  },
  loading: {
    textAlign: 'center',
    padding: '20px',
    color: theme.colors.secondary,
  },
  moreButton: {
    display: 'block',
    margin: '20px auto 0',
    padding: '10px 24px',
    borderRadius: '8px',
    border: `2px solid ${theme.colors.primary}`,
    background: 'white',
    color: theme.colors.primary,
    fontWeight: 'bold',
    cursor: 'pointer',
  },
};

export default PushHistory;
